import {skipToken} from '@reduxjs/toolkit/query';
import {
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Option,
  Tooltip,
} from 'platform/components';
import {Grid, GridItem, Show, Space, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {head} from 'ramda';
import {isArray, isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {useGetManufacturersQuery} from '@dms/api/core';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {useGetSupplierOrderTypeV3Query} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {FormToDataGridConnectorProps} from 'features/eag-grid';

import {useGetCurrentBranch} from '../../hooks/useGetCurrentBranch';
import {useGetSupplierCustomersQuery} from '../../hooks/useGetSupplierCustomersQuery';
import {useTenant} from '../../hooks/useTenant';
import {getOptionsFromAuthorizationProfiles} from '../../utils/getOptionsFromAuthorizationProfiles';
import {getOptionsFromCustomersAsSupplier} from '../../utils/getOptionsFromCustomersAsSupplier';
import {getOptionsFromManufacturers} from '../../utils/getOptionsFromManufacturers';
import {getSearchTypeOptions} from '../../utils/getSearchTypeOptions';

type PriceListExternalFiltersForm = Partial<{
  catalogNumber: string;
  isSearchSupplierOrderingNumber: boolean;
  searchType: string;
  name: string;
  manufacturerId: string;
  supplierId: string;
  supplierAccountCode: string;
  supplierOrderTypeCode: string;
  authorizationProfileId: string;
}>;

type PriceListExternalFiltersFormProps = FormToDataGridConnectorProps &
  TestIdProps & {
    // Both fields are optional inputs to the v3 supplier-order/type endpoint. When the filter is
    // used under an entity (supplier order, receive note), the entity's variant and authorization
    // profile scope the returned order types and supplier accounts. When omitted (standalone
    // price list, workshop, direct sale), the endpoint resolves them against the active branch
    // alone (sent automatically as the x-branch header), so no order variant has to be faked.
    // authorizationProfileId acts as a FALLBACK: a profile the user picks in the filter select
    // (when enabled) takes precedence over it.
    supplierOrderVariantId?: string | Nullish;
    authorizationProfileId?: string | Nullish;
    // Renders a single-choice authorization profile filter. With authorizationProfileId also
    // provided (entity-scoped grids), the select is prefilled with the entity's profile and is
    // not clearable — the consumer's queryModifier must then prefer the filter value over the
    // entity profile, otherwise the selection is silently overridden.
    hasAuthorizationProfileFilter?: boolean;
    // Branch whose authorization profiles populate the select and the default-profile fallback;
    // defaults to the active branch. Entity-scoped grids pass the entity's branch, which may
    // differ from the active one.
    branchId?: string | Nullish;
  };

const MINIMUM_LENGTH = 6;
const SEARCH_TYPES_WITH_MINIMUM_LENGTH = ['LIKE', 'STARTS_WITH', 'ENDS_WITH'];
const DEFAULT_SEARCH_TYPE = 'EQUALS';

const isMinLengthSearchType = (searchType: string | undefined) =>
  SEARCH_TYPES_WITH_MINIMUM_LENGTH.includes(searchType ?? '');

const buildOptions = (
  items:
    | Array<{label?: string | null | undefined; value?: string | null | undefined}>
    | null
    | undefined
): Option[] =>
  (items ?? []).reduce<Option[]>((opts, item) => {
    if (isNotNil(item.value) && isNotNil(item.label)) {
      opts.push({label: item.label, value: item.value});
    }
    return opts;
  }, []);

export function PriceListExternalFilters(props: PriceListExternalFiltersFormProps) {
  // Remount the stateful inner form whenever the externally-applied supplierId or
  // authorizationProfileId changes (e.g. saved-search restore or reset-all-filters), so the
  // mirrored state can never go stale relative to props. Internal selection flows through the
  // field onChange handlers, not props.values, so it does not trigger a remount.
  const externalSupplierId =
    typeof props.values.supplierId === 'string' ? props.values.supplierId : undefined;
  const externalAuthorizationProfileId =
    typeof props.values.authorizationProfileId === 'string'
      ? props.values.authorizationProfileId
      : undefined;

  return (
    <PriceListExternalFiltersInner
      key={JSON.stringify([externalSupplierId, externalAuthorizationProfileId])}
      {...props}
    />
  );
}

function PriceListExternalFiltersInner(props: PriceListExternalFiltersFormProps) {
  const [currentSupplierId, setCurrentSupplierId] = useState<string | undefined>(
    typeof props.values.supplierId === 'string' ? props.values.supplierId : undefined
  );
  const [currentAuthorizationProfileId, setCurrentAuthorizationProfileId] = useState<
    string | undefined
  >(
    typeof props.values.authorizationProfileId === 'string'
      ? props.values.authorizationProfileId
      : undefined
  );

  const {
    data: manufacturers,
    isLoading: areManufacturersLoading,
    isError: hasManufacturersError,
  } = useGetManufacturersQuery();

  const {
    data: suppliers,
    isLoading: areSuppliersLoading,
    isError: hasSuppliersError,
  } = useGetSupplierCustomersQuery();

  // The branch profile list feeds the filter select (when enabled) and the default-profile
  // fallback used when neither the user nor the consumer provides a profile.
  const hasProvidedAuthorizationProfileId = isNotNil(props.authorizationProfileId);
  const {activeBranchId} = useGetCurrentBranch();
  const {tenant} = useTenant();
  const tenantId = tenant?.id;

  const {data: branchAuthorizationProfiles} = useGetAuthorizationProfilesQuery(
    (props.hasAuthorizationProfileFilter || !hasProvidedAuthorizationProfileId) &&
      isNotNil(tenantId)
      ? {branchId: props.branchId ?? activeBranchId, 'x-tenant': tenantId}
      : skipToken
  );

  // Precedence: the profile the user picked in the filter wins over the entity-provided
  // fallback, which wins over the branch's default profile.
  const authorizationProfileId =
    currentAuthorizationProfileId ??
    props.authorizationProfileId ??
    branchAuthorizationProfiles?.find((profile) => profile.isDefault && profile.isActive)?.id;

  // v3 resolves order types and supplier accounts from the supplier plus the active branch
  // (x-branch header, injected by the base query); the order variant and authorization profile
  // are optional and only narrow the result further.
  const {data: supplierOrderTypeData} = useGetSupplierOrderTypeV3Query(
    currentSupplierId
      ? {
          supplierId: currentSupplierId,
          supplierOrderVariantId: props.supplierOrderVariantId ?? undefined,
          authorizationProfileId: authorizationProfileId ?? undefined,
        }
      : skipToken
  );

  const supplierAccountOptions = buildOptions(supplierOrderTypeData?.supplierAccount);
  const orderTypeOptions = buildOptions(supplierOrderTypeData?.orderType);

  const isLoading = areManufacturersLoading || areSuppliersLoading;
  const hasError = hasManufacturersError || hasSuppliersError;

  const searchType = isArray(props.values.searchType)
    ? head(props.values.searchType)
    : props.values.searchType;

  const appliedAuthorizationProfileId =
    typeof props.values.authorizationProfileId === 'string'
      ? props.values.authorizationProfileId
      : undefined;

  const values = {
    ...props.values,
    isSearchSupplierOrderingNumber: props.values.isSearchSupplierOrderingNumber === true,
    searchType: searchType ?? DEFAULT_SEARCH_TYPE,
    // When the filter select is shown for an entity-scoped grid, prefill it with the entity's
    // profile so the displayed value matches the profile the grid effectively queries with.
    ...(props.hasAuthorizationProfileFilter && {
      authorizationProfileId:
        appliedAuthorizationProfileId ?? props.authorizationProfileId ?? undefined,
    }),
  };

  const handleOnSubmit: FormSubmitHandler<PriceListExternalFiltersForm> = async (
    data,
    setErrors
  ) => {
    const searchType = data.searchType;
    const rawCatalogNumber = data.catalogNumber;
    const rawName = data.name;
    const catalogNumber = rawCatalogNumber?.trim() ?? '';
    const name = rawName?.trim() ?? '';

    // Validation rules:
    // - LIKE/STARTS_WITH/ENDS_WITH search types require catalogNumber >= 6 chars;
    //   shorter input auto-switches to EQUALS.
    // - At least one of catalogNumber or name must be filled.
    // - If catalogNumber is present but too short for the active search type,
    //   show a minimum-length error; otherwise show a "both fields required" error.
    const shouldAutoSwitchToEquals =
      isMinLengthSearchType(searchType) &&
      catalogNumber.length > 0 &&
      catalogNumber.length < MINIMUM_LENGTH;

    const effectiveSearchType = shouldAutoSwitchToEquals ? DEFAULT_SEARCH_TYPE : searchType;

    const isCatalogNumberNilOrEmpty = isNilOrEmpty(catalogNumber);
    const shouldApplyMinimumLengthValidation = isMinLengthSearchType(effectiveSearchType);
    const isCatalogNumberTooShort =
      shouldApplyMinimumLengthValidation &&
      catalogNumber.length > 0 &&
      catalogNumber.length < MINIMUM_LENGTH;
    const isNameNilOrEmpty = isNilOrEmpty(name);
    const isNameTooShort = name.length > 0 && name.length < MINIMUM_LENGTH;

    const isValidationError =
      (isCatalogNumberNilOrEmpty || isCatalogNumberTooShort) &&
      (isNameNilOrEmpty || isNameTooShort);

    if (!isValidationError) {
      await props.onChange({...data, searchType: effectiveSearchType});
      return;
    }

    match({isCatalogNumberTooShort, isNameTooShort})
      .with({isCatalogNumberTooShort: true}, () => {
        setErrors([
          {
            name: 'catalogNumber',
            message: i18n.t('page.warehousePriceList.label.minimumLengthIsSixthCharacters'),
          },
        ]);
      })
      .with({isNameTooShort: true}, () => {
        setErrors([
          {
            name: 'name',
            message: i18n.t('page.warehousePriceList.label.minimumLengthIsSixthCharacters'),
          },
        ]);
      })
      .otherwise(() => {
        setErrors([
          {
            name: 'catalogNumber',
            message: i18n.t('page.warehousePriceList.label.warehouseValidationMessage'),
          },
          {
            name: 'name',
            message: i18n.t('page.warehousePriceList.label.warehouseValidationMessage'),
          },
        ]);
      });
  };

  return (
    <DataStatus isLoading={isLoading} isError={hasError}>
      <Form<PriceListExternalFiltersForm>
        defaultValues={{isSearchSupplierOrderingNumber: false}}
        values={values}
        onSubmit={handleOnSubmit}
      >
        {(control, formApi) => {
          const rawCatalogNumber = formApi.watch('catalogNumber');
          const searchType = formApi.watch('searchType');
          const rawName = formApi.watch('name');
          const supplierId = formApi.watch('supplierId');
          const catalogNumber = rawCatalogNumber?.trim() ?? '';
          const name = rawName?.trim() ?? '';

          const shouldDisableMinLengthTypes = catalogNumber.length < MINIMUM_LENGTH;
          const filteringMethodOptions = getSearchTypeOptions(shouldDisableMinLengthTypes);

          const shouldAutoSwitchToEquals =
            shouldDisableMinLengthTypes && isMinLengthSearchType(searchType);

          if (shouldAutoSwitchToEquals) {
            formApi.setValue('searchType', DEFAULT_SEARCH_TYPE);
          }

          const isCatalogNumberNilOrEmpty = isNilOrEmpty(catalogNumber);
          const shouldApplyMinimumLengthValidation = isMinLengthSearchType(searchType);
          const isCatalogNumberTooShort =
            shouldApplyMinimumLengthValidation &&
            catalogNumber.length > 0 &&
            catalogNumber.length < MINIMUM_LENGTH;
          const isNameNilOrEmpty = isNilOrEmpty(name);
          const isNameTooShort = name.length > 0 && name.length < MINIMUM_LENGTH;

          const isValidationError =
            (isCatalogNumberNilOrEmpty || isCatalogNumberTooShort) &&
            (isNameNilOrEmpty || isNameTooShort);

          match({isValidationError, isCatalogNumberTooShort, isNameTooShort})
            .with({isValidationError: true, isCatalogNumberTooShort: true}, () => {
              formApi.setError('catalogNumber', {
                message: i18n.t('page.warehousePriceList.label.minimumLengthIsSixthCharacters'),
              });
              formApi.clearErrors('name');
            })
            .with({isValidationError: true, isNameTooShort: true}, () => {
              formApi.clearErrors('catalogNumber');
              formApi.setError('name', {
                message: i18n.t('page.warehousePriceList.label.minimumLengthIsSixthCharacters'),
              });
            })
            .with({isValidationError: true}, () => {
              formApi.setError('catalogNumber', {
                message: i18n.t('page.warehousePriceList.label.warehouseValidationMessage'),
              });
              formApi.setError('name', {
                message: i18n.t('page.warehousePriceList.label.warehouseValidationMessage'),
              });
            })
            .otherwise(() => {
              formApi.clearErrors();
            });

          // The account/order-type options returned by v3 supplier-order/type are scoped by
          // both the supplier and the authorization profile, so changing either invalidates
          // the dependent selections.
          const clearSupplierScopedFields = () => {
            formApi.setValue('supplierAccountCode', undefined);
            formApi.setValue('supplierOrderTypeCode', undefined);
          };

          const handleSupplierChange = (newSupplierId: string | number | null) => {
            clearSupplierScopedFields();
            setCurrentSupplierId(typeof newSupplierId === 'string' ? newSupplierId : undefined);
          };

          const handleAuthorizationProfileChange = (newProfileId: string | number | null) => {
            clearSupplierScopedFields();
            setCurrentAuthorizationProfileId(
              typeof newProfileId === 'string' ? newProfileId : undefined
            );
          };

          const isSupplierSelected = typeof supplierId === 'string' && !isNilOrEmpty(supplierId);

          return (
            <VStack spacing={4}>
              <Grid columns={3} align={isValidationError ? 'flex-start' : 'flex-end'}>
                <GridItem span={2}>
                  <FormField
                    control={control}
                    name="catalogNumber"
                    type="text"
                    label={i18n.t('entity.warehouse.labels.catalogueNumber')}
                    data-testid={suffixTestId('inputs.catalogueNumber', props)}
                  />
                </GridItem>
                <GridItem span={1}>
                  <Show when={isValidationError}>
                    <Space vertical={5} />
                  </Show>
                  <FormField
                    control={control}
                    name="searchType"
                    type="choice"
                    options={filteringMethodOptions}
                    isNotClearable
                    formatOptionLabel={(option, formatOptionLabelMeta) => {
                      if (formatOptionLabelMeta.context === 'menu' && option.isDisabled) {
                        return (
                          // eslint-disable-next-line react/forbid-dom-props -- Pre-existing violation
                          <div style={{pointerEvents: 'auto'}}>
                            <Tooltip
                              label={i18n.t(
                                'page.warehousePriceList.label.minimumLengthIsSixthCharacters'
                              )}
                            >
                              <span>{option.label}</span>
                            </Tooltip>
                          </div>
                        );
                      }
                      return option.label;
                    }}
                    data-testid={suffixTestId('inputs.filteringMethod', props)}
                  />
                  <Show when={isValidationError}>
                    <Space vertical={8} />
                  </Show>
                </GridItem>
              </Grid>
              <FormField
                control={control}
                name="isSearchSupplierOrderingNumber"
                type="checkbox"
                label={i18n.t('entity.warehouse.labels.searchSupplierOrderingNumber')}
                data-testid={suffixTestId('inputs.isSearchSupplierOrderingNumber', props)}
              />
              <FormField
                control={control}
                name="name"
                type="text"
                label={i18n.t('entity.warehouse.labels.name')}
                data-testid={suffixTestId('inputs.name', props)}
              />
              <FormField
                control={control}
                name="manufacturerId"
                type="multiChoice"
                label={i18n.t('entity.warehouse.labels.manufacturer')}
                options={getOptionsFromManufacturers(manufacturers)}
                data-testid={suffixTestId('inputs.manufacturer', props)}
              />
              <FormField
                control={control}
                name="supplierId"
                type="choice"
                label={i18n.t('entity.warehouse.labels.supplier')}
                options={getOptionsFromCustomersAsSupplier(suppliers)}
                onChange={handleSupplierChange}
                data-testid={suffixTestId('inputs.supplier', props)}
              />
              <Show when={props.hasAuthorizationProfileFilter}>
                <FormField
                  control={control}
                  name="authorizationProfileId"
                  type="choice"
                  label={i18n.t('entity.warehouse.labels.authorizationProfile')}
                  options={getOptionsFromAuthorizationProfiles(
                    branchAuthorizationProfiles,
                    formApi.watch('authorizationProfileId')
                  )}
                  onChange={handleAuthorizationProfileChange}
                  // On entity-scoped grids clearing would just fall back to the entity's
                  // profile anyway, so keep the effective profile always visible.
                  isNotClearable={hasProvidedAuthorizationProfileId}
                  data-testid={suffixTestId('inputs.authorizationProfile', props)}
                />
              </Show>
              <FormField
                control={control}
                name="supplierAccountCode"
                type="choice"
                label={i18n.t('entity.warehouse.labels.supplierAccount')}
                options={supplierAccountOptions}
                isDisabled={!isSupplierSelected}
                data-testid={suffixTestId('inputs.supplierAccount', props)}
              />
              <FormField
                control={control}
                name="supplierOrderTypeCode"
                type="choice"
                label={i18n.t('entity.warehouse.labels.supplierOrderType')}
                options={orderTypeOptions}
                isDisabled={!isSupplierSelected}
                data-testid={suffixTestId('inputs.supplierOrderTypeCode', props)}
              />
              <Grid columns={4}>
                <GridItem span={2}>
                  <FormButton
                    control={control}
                    type="submit"
                    variant="secondary"
                    title={i18n.t('general.actions.apply')}
                    data-testid={suffixTestId('apply', props)}
                  />
                </GridItem>
              </Grid>
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}
